import { useEffect, useState, useMemo } from "react";
import { causasFalla } from "./causas";
import { puedeEditarModulo } from "./permisos";

const CLARO_RED  = "#E30613";
const CLARO_DARK = "#0f172a";
const VERDE      = "#16a34a";

function BarChart({ items, colorSi="#16a34a", colorPend="#f59e0b", colorNo="#e30613", labelWidth="140px", onClickItem, onClickSegment, activeConciliado="" }) {
  const maxVal = Math.max(...items.map(it => it.total), 1);
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"5px" }}>
      {items.map((it, i) => (
        <div key={i}
          style={{ display:"flex", alignItems:"center", gap:"6px", fontSize:"11px",
                   borderRadius:"5px", padding:"2px 4px", transition:"background 0.15s" }}>
          {/* Label — filtra por label */}
          <div
            onClick={() => onClickItem && onClickItem(it.label)}
            style={{ width:labelWidth, textAlign:"right", color:"#475569", fontWeight:"600",
                     whiteSpace:"normal", lineHeight:"1.2", wordBreak:"break-word", flexShrink:0,
                     cursor: onClickItem ? "pointer" : "default" }}
            onMouseEnter={e => { if (onClickItem) e.currentTarget.style.color="#0f172a"; }}
            onMouseLeave={e => { e.currentTarget.style.color="#475569"; }}>
            {it.label}
          </div>
          {/* Barra segmentada — cada segmento filtra por estado */}
          <div style={{ flex:1, height:"16px", background:"#f1f5f9", borderRadius:"3px", overflow:"hidden", display:"flex" }}>
            {it.si > 0 && (
              <div
                onClick={() => onClickSegment && onClickSegment(it.label, "SI")}
                title={`Conciliados: ${it.si}`}
                style={{ width:`${(it.si/maxVal)*100}%`, background:colorSi,
                         cursor: onClickSegment ? "pointer" : "default",
                         opacity: activeConciliado === "" || activeConciliado === "SI" ? 1 : 0.35,
                         transition:"opacity 0.15s" }} />
            )}
            {it.pend > 0 && (
              <div
                onClick={() => onClickSegment && onClickSegment(it.label, "PEND")}
                title={`Pendientes: ${it.pend}`}
                style={{ width:`${(it.pend/maxVal)*100}%`, background:colorPend,
                         cursor: onClickSegment ? "pointer" : "default",
                         opacity: activeConciliado === "" || activeConciliado === "PEND" ? 1 : 0.35,
                         transition:"opacity 0.15s" }} />
            )}
            {(it.no||0) > 0 && (
              <div
                onClick={() => onClickSegment && onClickSegment(it.label, "NO")}
                title={`No conciliados: ${it.no}`}
                style={{ width:`${((it.no||0)/maxVal)*100}%`, background:colorNo,
                         cursor: onClickSegment ? "pointer" : "default",
                         opacity: activeConciliado === "" || activeConciliado === "NO" ? 1 : 0.35,
                         transition:"opacity 0.15s" }} />
            )}
          </div>
          <div style={{ width:"24px", textAlign:"right", fontWeight:"700", color:"#1e293b", fontSize:"11px", flexShrink:0 }}>{it.total}</div>
          <div style={{ width:"34px", textAlign:"right", fontWeight:"700", fontSize:"10px", flexShrink:0, color: it.si/it.total >= 0.8 ? colorSi : colorPend }}>
            {it.total > 0 ? `${Math.round(it.si/it.total*100)}%` : "—"}
          </div>
        </div>
      ))}
    </div>
  );
}

function DonutChart({ conciliados, pendientes, noConciliados, total, pct, onClickSegment }) {
  const r = 54, cx = 70, cy = 70, sw = 18;
  const circ = 2 * Math.PI * r;
  const segs = [
    { val: conciliados,   color:"#16a34a", key:"SI" },
    { val: pendientes,    color:"#f59e0b", key:"PEND" },
    { val: noConciliados, color:"#e30613", key:"NO" },
  ];
  let off = 0;
  return (
    <svg width="140" height="140" viewBox="0 0 140 140" style={{ cursor: onClickSegment ? "pointer" : "default" }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f1f5f9" strokeWidth={sw}/>
      {segs.map((s, i) => {
        if (!s.val) return null;
        const dash = (s.val / Math.max(total,1)) * circ;
        const el = <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color} strokeWidth={sw}
          strokeDasharray={`${dash} ${circ-dash}`} strokeDashoffset={-off} transform={`rotate(-90 ${cx} ${cy})`}
          style={{ cursor: onClickSegment ? "pointer" : "default" }}
          onClick={() => onClickSegment && onClickSegment(s.key)}
        />;
        off += dash;
        return el;
      })}
      <text x={cx} y={cy-6} textAnchor="middle" fontSize="20" fontWeight="800" fill="#1e293b">{pct}%</text>
      <text x={cx} y={cy+12} textAnchor="middle" fontSize="9" fill="#64748b">Conciliados</text>
    </svg>
  );
}

function ConciliacionesOfensores({ usuario }) {
  const [data, setData]             = useState([]);
  const [cargando, setCargando]     = useState(true);
  const [editando, setEditando]     = useState({}); // { id: { campo: valor } }

  // Filtros
  const [busquedaNodo, setBusquedaNodo] = useState("");
  const [filtroMes,    setFiltroMes]    = useState([]);
  const [filtroAliado, setFiltroAliado] = useState([]);
  const [filtroCiudad, setFiltroCiudad] = useState([]);
  const [filtroJefe,   setFiltroJefe]   = useState([]);
  const [filtroCateg,  setFiltroCateg]  = useState([]);
  const [filtroConciliado, setFiltroConciliado] = useState(""); // "SI" | "NO" | ""
  const [filtroTipoCon,    setFiltroTipoCon]    = useState(""); // texto libre

  const [paginaActual, setPaginaActual] = useState(1);
  const FILAS_POR_PAGINA = 50;

  const puedeEditar = puedeEditarModulo(usuario, "conciliaciones");

  // ── Cargar datos ────────────────────────────────────────────
  const cargar = () => {
    setCargando(true);
    fetch(`${process.env.REACT_APP_API_URL}/conciliaciones`)
      .then(res => res.json())
      .then(rows => { setData(rows); setCargando(false); })
      .catch(() => setCargando(false));
  };

  useEffect(() => { cargar(); }, []);

  // ── Edición inline ──────────────────────────────────────────
  const getVal = (id, campo) => editando[id]?.[campo] ?? null;

  const actualizarLocal = (id, campo, valor) => {
    setEditando(prev => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [campo]: valor }
    }));
  };

  const guardarCambio = (id, campo, valorDirecto) => {
    const row = data.find(r => r.id === id);
    if (!row) return;
    const v = (c) => c === campo ? (valorDirecto ?? getVal(id, c) ?? row[c]) : (getVal(id, c) ?? row[c]);
    if (!puedeEditar) return;
    const body = {
      causa_falla:        v("causa_falla"),
      observaciones:      v("observaciones"),
      conciliado:         v("conciliado"),
      tipo_conciliacion:  v("tipo_conciliacion"),
      categoria_concilia: v("categoria_concilia"),
      obs_conciliacion:   v("obs_conciliacion"),
      rol: usuario?.rol, cedula: usuario?.cedula,
    };
    // Optimistic update en data local
    setData(prev => prev.map(r => r.id === id ? { ...r, ...body } : r));
    fetch(`${process.env.REACT_APP_API_URL}/conciliaciones/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    })
    .then(res => { if (!res.ok) console.error("Error guardando conciliación"); })
    .catch(err => console.error(err));
  };

  // ── Eliminar ────────────────────────────────────────────────
  const eliminar = (id) => {
    if (!puedeEditar) return;
    if (!window.confirm("¿Eliminar este registro de conciliaciones?")) return;
    fetch(`${process.env.REACT_APP_API_URL}/conciliaciones/${id}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rol: usuario?.rol, cedula: usuario?.cedula }),
    })
      .then(res => res.json())
      .then(resp => { if (resp.ok) setData(prev => prev.filter(r => r.id !== id)); else console.error(resp.mensaje); })
      .catch(err => console.error(err));
  };

  // ── Formato fecha ───────────────────────────────────────────
  const formatFechaConciliacion = (f) => {
    if (!f) return "—";
    if (typeof f === "string" && f.match(/^\d{2}\/\d{2}\/\d{4}/)) return f.substring(0, 10);
    const d = new Date(f);
    if (!isNaN(d.getTime())) return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
    return f;
  };

  const formatMes = (f) => {
    if (!f) return "—";
    if (f.includes("T")) { const [a, m] = f.split("T")[0].split("-"); return `${m}-${a}`; }
    if (f.length === 10)  { const [a, m] = f.split("-"); return `${m}-${a}`; }
    return f;
  };

  // ── Opciones filtros ────────────────────────────────────────
  const opcionesMes    = useMemo(() => [...new Set(data.map(r => formatMes(r.mes_anio)).filter(Boolean))].sort().reverse(), [data]);
  const opcionesAliado = useMemo(() => [...new Set(data.map(r => r.aliado).filter(Boolean))].sort(), [data]);
  const opcionesCiudad = useMemo(() => [...new Set(data.map(r => r.ciudad).filter(Boolean))].sort(), [data]);
  const opcionesJefe   = useMemo(() => [...new Set(data.map(r => r.nombre_jefe).filter(Boolean))].sort(), [data]);
  const opcionesCateg  = useMemo(() => [...new Set(data.map(r => r.categoria).filter(Boolean))].sort(), [data]);

  // ── Filtrado ────────────────────────────────────────────────
  const datosFiltrados = useMemo(() => {
    return data.filter(r => {
      const mesFmt = formatMes(r.mes_anio);
      if (busquedaNodo && !r.id_nodo?.toLowerCase().includes(busquedaNodo.toLowerCase()) &&
          !r.NOMBRE_NODO?.toLowerCase().includes(busquedaNodo.toLowerCase())) return false;
      if (filtroMes.length    && !filtroMes.includes(mesFmt))          return false;
      if (filtroAliado.length && !filtroAliado.includes(r.aliado))     return false;
      if (filtroCiudad.length && !filtroCiudad.includes(r.ciudad))     return false;
      if (filtroJefe.length   && !filtroJefe.includes(r.nombre_jefe))  return false;
      if (filtroCateg.length  && !filtroCateg.includes(r.categoria))   return false;
      if (filtroConciliado === "SI"   && r.conciliado !== "SI")                        return false;
      if (filtroConciliado === "NO"   && r.conciliado !== "NO")                        return false;
      if (filtroConciliado === "PEND" && r.conciliado !== "" && r.conciliado != null)  return false;
      if (filtroTipoCon    !== "" && (r.tipo_conciliacion || "") !== filtroTipoCon) return false;
      return true;
    });
  }, [data, busquedaNodo, filtroMes, filtroAliado, filtroCiudad, filtroJefe, filtroCateg, filtroConciliado, filtroTipoCon]);

  const totalPags   = Math.max(1, Math.ceil(datosFiltrados.length / FILAS_POR_PAGINA));
  const datosPagina = datosFiltrados.slice((paginaActual - 1) * FILAS_POR_PAGINA, paginaActual * FILAS_POR_PAGINA);

  const limpiarFiltros = () => {
    setBusquedaNodo(""); setFiltroMes([]); setFiltroAliado([]);
    setFiltroCiudad([]); setFiltroJefe([]); setFiltroCateg([]);
    setFiltroConciliado(""); setFiltroTipoCon("");
    setPaginaActual(1);
  };

  const hayFiltros = busquedaNodo || filtroMes.length || filtroAliado.length ||
                     filtroCiudad.length || filtroJefe.length || filtroCateg.length ||
                     filtroConciliado || filtroTipoCon;

  // ── Descargar CSV ───────────────────────────────────────────
  const descargarCSV = () => {
    const sep = ";";
    const esc = v => {
      const s = (v ?? "").toString().replace(/"/g, '""').replace(/\n/g, " ");
      return `"${s}"`;
    };
    const cols = [
      "ID Nodo", "Nodo", "Ciudad", "Categoría", "% Dispo Real",
      "Mes", "Aliado", "Jefe Zona", "Agrupación", "Prioridad",
      "Causa Falla", "Observaciones",
      "Conciliado", "Tipo Conciliación", "Cat. Conciliación", "Obs. Conciliación",
      "Enviado por", "Fecha Conciliación"
    ];
    const filas = datosFiltrados.map(r => [
      esc(r.id_nodo),
      esc(r.NOMBRE_NODO),
      esc(r.ciudad),
      esc(r.categoria),
      r.porc_dispo_real != null ? parseFloat(r.porc_dispo_real).toFixed(2).replace(".", ",") : "",
      esc(formatMes(r.mes_anio)),
      esc(r.aliado),
      esc(r.nombre_jefe),
      esc(r.agrupacion),
      esc(r.tipo_prioridad),
      esc(r.causa_falla),
      esc(r.observaciones),
      esc(r.conciliado),
      esc(r.tipo_conciliacion),
      esc(r.categoria_concilia),
      esc(r.obs_conciliacion),
      esc(r.nombre_usuario),
      esc(formatFechaConciliacion(r.fecha_conciliacion)),
    ].join(sep));
    const csv = "\uFEFF" + [cols.map(c => `"${c}"`).join(sep), ...filas].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `conciliaciones_ofensores_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // ── KPIs para gráficas ──────────────────────────────────────
  const kpis = useMemo(() => {
    const total = datosFiltrados.length;
    const conciliados   = datosFiltrados.filter(r => r.conciliado === "SI").length;
    const pendientes    = datosFiltrados.filter(r => !r.conciliado || r.conciliado === "").length;
    const noConciliados = datosFiltrados.filter(r => r.conciliado === "NO").length;
    const pct = total > 0 ? Math.round(conciliados/total*100) : 0;
    const porAliado = {};
    datosFiltrados.forEach(r => {
      const a = r.aliado || "Sin aliado";
      if (!porAliado[a]) porAliado[a] = { total:0, si:0, no:0, pend:0 };
      porAliado[a].total++;
      if (r.conciliado === "SI") porAliado[a].si++;
      else if (r.conciliado === "NO") porAliado[a].no++;
      else porAliado[a].pend++;
    });
    const porMes = {};
    datosFiltrados.forEach(r => {
      const m = formatMes(r.mes_anio) || "Sin mes";
      if (!porMes[m]) porMes[m] = { total:0, si:0, pend:0, no:0 };
      porMes[m].total++;
      if (r.conciliado === "SI") porMes[m].si++;
      else if (r.conciliado === "NO") porMes[m].no++;
      else porMes[m].pend++;
    });
    // Por tipo de conciliación
    const porTipo = {};
    datosFiltrados.forEach(r => {
      const t = r.tipo_conciliacion || "Sin definir";
      if (!porTipo[t]) porTipo[t] = { total:0, si:0, no:0, pend:0 };
      porTipo[t].total++;
      if (r.conciliado === "SI") porTipo[t].si++;
      else if (r.conciliado === "NO") porTipo[t].no++;
      else porTipo[t].pend++;
    });
    // Por jefe de zona
    const porJefe = {};
    datosFiltrados.forEach(r => {
      const j = r.nombre_jefe || "Sin jefe";
      if (!porJefe[j]) porJefe[j] = { total:0, si:0, no:0, pend:0 };
      porJefe[j].total++;
      if (r.conciliado === "SI") porJefe[j].si++;
      else if (r.conciliado === "NO") porJefe[j].no++;
      else porJefe[j].pend++;
    });
    return { total, conciliados, pendientes, noConciliados, pct, porAliado, porMes, porTipo, porJefe };
  }, [datosFiltrados]);

  const thSt = { padding: "8px 10px", background: "#1e293b", color: "white", fontSize: "11px",
                 textAlign: "left", fontWeight: "700", whiteSpace: "nowrap", position: "sticky", top: 0 };

  // ── Render ──────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", color: CLARO_DARK }}>

      {/* Header */}
      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: "12px",
                    padding: "16px 20px", marginBottom: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "4px", height: "28px", background: CLARO_RED, borderRadius: "2px" }} />
            <div>
              <div style={{ fontSize: "16px", fontWeight: "800", color: CLARO_DARK }}>Conciliaciones Ofensores</div>
              <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                Registros enviados desde Panel Ofensores N2+ para conciliación. Independientes del origen.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <span style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "20px",
                           padding: "4px 12px", fontSize: "12px", color: "#475569", fontWeight: "600" }}>
              {datosFiltrados.length} registros
            </span>
            <button onClick={cargar} style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "8px",
                                              padding: "7px 14px", fontSize: "12px", fontWeight: "600", cursor: "pointer", color: "#475569" }}>
              🔄 Recargar
            </button>
            <button onClick={descargarCSV} style={{ background: VERDE, color: "white", border: "none",
                                                    borderRadius: "8px", padding: "7px 14px", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>
              ⬇️ Descargar CSV
            </button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: "12px",
                    padding: "16px 20px", marginBottom: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ width: "3px", height: "18px", background: CLARO_RED, borderRadius: "2px" }} />
            <span style={{ fontSize: "13px", fontWeight: "700", color: CLARO_DARK, textTransform: "uppercase", letterSpacing: "0.05em" }}>Filtros</span>
          </div>
          {hayFiltros && (
            <button onClick={limpiarFiltros} style={{ background: "#fef2f2", border: "1px solid #fecaca",
                                                      borderRadius: "20px", padding: "4px 12px", fontSize: "12px",
                                                      color: CLARO_RED, fontWeight: "600", cursor: "pointer" }}>
              ✕ Limpiar filtros
            </button>
          )}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-start" }}>
          {/* Buscar */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: "150px" }}>
            <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>Buscar nodo</label>
            <input type="text" placeholder="Escribe un nodo..." value={busquedaNodo}
              onChange={e => { setBusquedaNodo(e.target.value); setPaginaActual(1); }}
              style={{ padding: "7px 10px", borderRadius: "7px", border: busquedaNodo ? `1.5px solid ${CLARO_RED}` : "1.5px solid #e2e8f0",
                       fontSize: "12px", background: "#f8fafc", outline: "none", width: "100%", boxSizing: "border-box" }} />
          </div>
          {/* Mes */}
          {[
            { label: "Mes",       opts: opcionesMes,    val: filtroMes,    set: setFiltroMes,    w: "110px" },
            { label: "Aliado",    opts: opcionesAliado, val: filtroAliado, set: setFiltroAliado, w: "130px" },
            { label: "Ciudad",    opts: opcionesCiudad, val: filtroCiudad, set: setFiltroCiudad, w: "160px" },
            { label: "Jefe Zona", opts: opcionesJefe,   val: filtroJefe,   set: setFiltroJefe,   w: "220px" },
            { label: "Categoría", opts: opcionesCateg,  val: filtroCateg,  set: setFiltroCateg,  w: "80px"  },
          ].map(({ label, opts, val, set, w }) => (
            <div key={label} style={{ display: "flex", flexDirection: "column", gap: "4px", width: w }}>
              <label style={{ fontSize: "10px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>{label}</label>
              <select multiple size={Math.min(opts.length, 5)} value={val}
                onChange={e => { set(Array.from(e.target.selectedOptions).map(o => o.value)); setPaginaActual(1); }}
                style={{ padding: "7px 10px", borderRadius: "7px", border: val.length ? `1.5px solid ${CLARO_RED}` : "1.5px solid #e2e8f0",
                         fontSize: "12px", background: "#f8fafc", width: "100%" }}>
                {opts.map((o, i) => <option key={i} value={o}>{o}</option>)}
              </select>
              {val.length > 0 && <button onClick={() => set([])} style={{ fontSize: "11px", color: CLARO_RED, background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}>✕ Limpiar</button>}
            </div>
          ))}
        </div>
      </div>

      {/* Tabla */}
      {/* ── Gráficas gerenciales ─────────────────────────────── */}
      {!cargando && datosFiltrados.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1.6fr", gap:"12px", marginBottom:"12px" }}>
          {/* Gráfica 1: Donut */}
          <div style={{ background:"white", borderRadius:"12px", padding:"14px 18px", border:"1px solid #e2e8f0", boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"12px" }}>
              <div style={{ width:"3px", height:"16px", background:CLARO_RED, borderRadius:"2px" }} />
              <span style={{ fontSize:"11px", fontWeight:"800", color:CLARO_DARK, textTransform:"uppercase", letterSpacing:"0.05em" }}>Estado Global</span>
              <span style={{ fontSize:"10px", color:"#94a3b8", fontStyle:"italic" }}>— clic para filtrar</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:"16px" }}>
              <DonutChart {...kpis}
                onClickSegment={(key) => {
                  // Filtra la tabla por conciliado: toggle
                  // Usamos busquedaNodo como proxy no — usamos filtroCateg para "conciliado"
                  // En realidad no hay filtro de conciliado en los filtros actuales;
                  // lo implementamos directamente sobre el estado global de filtros via un nuevo filtro
                  // Por ahora buscamos en nodo no — aplicamos al filtro existente de mes vacío para "trigger"
                  // Lo mejor: agregar filtroConc al estado
                  setFiltroConciliado(prev => prev === key ? "" : key);
                  setPaginaActual(1);
                }}
              />
              <div style={{ flex:1 }}>
                {[
                  { label:"Conciliados",    val:kpis.conciliados,    color:"#16a34a", bg:"#dcfce7", key:"SI"    },
                  { label:"Pendientes",     val:kpis.pendientes,     color:"#d97706", bg:"#fef3c7", key:"PEND"  },
                  { label:"No conciliados", val:kpis.noConciliados,  color:CLARO_RED, bg:"#fef2f2", key:"NO"    },
                ].map((item,i) => (
                  <div key={i}
                    onClick={() => { setFiltroConciliado(prev => prev === item.key ? "" : item.key); setPaginaActual(1); }}
                    style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                             background: filtroConciliado === item.key ? item.color+"33" : item.bg,
                             borderRadius:"6px", padding:"5px 8px", marginBottom:"4px",
                             cursor:"pointer", border: filtroConciliado === item.key ? `2px solid ${item.color}` : "2px solid transparent",
                             transition:"all 0.15s" }}>
                    <span style={{ fontSize:"11px", color:"#475569", fontWeight:"600" }}>{item.label}</span>
                    <span style={{ fontSize:"14px", fontWeight:"800", color:item.color }}>{item.val} <span style={{ fontSize:"10px", color:"#94a3b8" }}>{kpis.total>0?`${Math.round(item.val/kpis.total*100)}%`:""}</span></span>
                  </div>
                ))}
                <div style={{ borderTop:"1px solid #f1f5f9", paddingTop:"6px", display:"flex", justifyContent:"space-between" }}>
                  <span style={{ fontSize:"10px", color:"#94a3b8", fontWeight:"600" }}>TOTAL</span>
                  <span style={{ fontSize:"16px", fontWeight:"800", color:CLARO_DARK }}>{kpis.total}</span>
                </div>
              </div>
            </div>
          </div>
          {/* Gráfica 2: Barras */}
          <div style={{ background:"white", borderRadius:"12px", padding:"14px 18px", border:"1px solid #e2e8f0", boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"6px" }}>
              <div style={{ width:"3px", height:"16px", background:CLARO_RED, borderRadius:"2px" }} />
              <span style={{ fontSize:"11px", fontWeight:"800", color:CLARO_DARK, textTransform:"uppercase", letterSpacing:"0.05em" }}>Avance por Aliado y Mes</span>
              <span style={{ fontSize:"10px", color:"#94a3b8", fontStyle:"italic" }}>— clic para filtrar</span>
            </div>
            <div style={{ display:"flex", gap:"10px", marginBottom:"8px", marginLeft:"148px" }}>
              {[["#16a34a","Conciliado"],["#f59e0b","Pendiente"],["#e30613","No"]].map(([c,l])=>(
                <div key={l} style={{ display:"flex", alignItems:"center", gap:"4px" }}>
                  <div style={{ width:"8px", height:"8px", background:c, borderRadius:"2px" }} />
                  <span style={{ fontSize:"10px", color:"#64748b", fontWeight:"600" }}>{l}</span>
                </div>
              ))}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
              <div>
                <div style={{ fontSize:"10px", fontWeight:"700", color:"#94a3b8", marginBottom:"6px", marginLeft:"148px", textTransform:"uppercase" }}>Por Aliado</div>
                <BarChart labelWidth="140px"
                  items={Object.entries(kpis.porAliado).sort((a,b)=>b[1].total-a[1].total).map(([label,v])=>({label,...v}))}
                  activeConciliado={filtroConciliado}
                  onClickItem={label => { setFiltroAliado(prev => prev.includes(label) ? prev.filter(x=>x!==label) : [...prev, label]); setPaginaActual(1); }}
                  onClickSegment={(label, estado) => {
                    setFiltroAliado(prev => prev.includes(label) ? prev.filter(x=>x!==label) : [...prev, label]);
                    setFiltroConciliado(prev => prev === estado ? "" : estado);
                    setPaginaActual(1);
                  }}
                />
              </div>
              <div>
                <div style={{ fontSize:"10px", fontWeight:"700", color:"#94a3b8", marginBottom:"6px", marginLeft:"148px", textTransform:"uppercase" }}>Por Mes</div>
                <BarChart labelWidth="140px"
                  items={Object.entries(kpis.porMes).sort((a,b)=>b[0].localeCompare(a[0])).slice(0,6).map(([label,v])=>({label,...v}))}
                  activeConciliado={filtroConciliado}
                  onClickItem={label => { setFiltroMes(prev => prev.includes(label) ? prev.filter(x=>x!==label) : [...prev, label]); setPaginaActual(1); }}
                  onClickSegment={(label, estado) => {
                    setFiltroMes(prev => prev.includes(label) ? prev.filter(x=>x!==label) : [...prev, label]);
                    setFiltroConciliado(prev => prev === estado ? "" : estado);
                    setPaginaActual(1);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Segunda fila de gráficas ──────────────────────────── */}
      {!cargando && datosFiltrados.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px", marginBottom:"12px" }}>

          {/* Gráfica 3: Tipo de Conciliación */}
          <div style={{ background:"white", borderRadius:"12px", padding:"14px 18px", border:"1px solid #e2e8f0", boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"8px" }}>
              <div style={{ width:"3px", height:"16px", background:"#7c3aed", borderRadius:"2px" }} />
              <span style={{ fontSize:"11px", fontWeight:"800", color:CLARO_DARK, textTransform:"uppercase", letterSpacing:"0.05em" }}>Por Tipo de Conciliación</span>
              <span style={{ fontSize:"10px", color:"#94a3b8", fontStyle:"italic" }}>— clic para filtrar</span>
            </div>
            <div style={{ display:"flex", gap:"10px", marginBottom:"8px", marginLeft:"188px" }}>
              {[["#16a34a","Conciliado"],["#f59e0b","Pendiente"],["#e30613","No"]].map(([c,l])=>(
                <div key={l} style={{ display:"flex", alignItems:"center", gap:"4px" }}>
                  <div style={{ width:"8px", height:"8px", background:c, borderRadius:"2px" }} />
                  <span style={{ fontSize:"10px", color:"#64748b", fontWeight:"600" }}>{l}</span>
                </div>
              ))}
            </div>
            {Object.keys(kpis.porTipo).length === 0
              ? <div style={{ color:"#94a3b8", fontSize:"12px", textAlign:"center", padding:"16px" }}>Sin tipos asignados aún</div>
              : <BarChart labelWidth="180px"
                  items={Object.entries(kpis.porTipo).sort((a,b)=>b[1].total-a[1].total).map(([label,v])=>({label,...v}))}
                  activeConciliado={filtroConciliado}
                  onClickItem={label => { setFiltroTipoCon(prev => prev === label ? "" : label); setPaginaActual(1); }}
                  onClickSegment={(label, estado) => {
                    setFiltroTipoCon(prev => prev === label ? "" : label);
                    setFiltroConciliado(prev => prev === estado ? "" : estado);
                    setPaginaActual(1);
                  }}
                />
            }
          </div>

          {/* Gráfica 4: Por Jefe de Zona */}
          <div style={{ background:"white", borderRadius:"12px", padding:"14px 18px", border:"1px solid #e2e8f0", boxShadow:"0 1px 4px rgba(0,0,0,0.06)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"8px" }}>
              <div style={{ width:"3px", height:"16px", background:"#0369a1", borderRadius:"2px" }} />
              <span style={{ fontSize:"11px", fontWeight:"800", color:CLARO_DARK, textTransform:"uppercase", letterSpacing:"0.05em" }}>Por Jefe de Zona</span>
              <span style={{ fontSize:"10px", color:"#94a3b8", fontStyle:"italic" }}>— clic para filtrar</span>
            </div>
            <div style={{ display:"flex", gap:"10px", marginBottom:"8px", marginLeft:"188px" }}>
              {[["#16a34a","Conciliado"],["#f59e0b","Pendiente"],["#e30613","No"]].map(([c,l])=>(
                <div key={l} style={{ display:"flex", alignItems:"center", gap:"4px" }}>
                  <div style={{ width:"8px", height:"8px", background:c, borderRadius:"2px" }} />
                  <span style={{ fontSize:"10px", color:"#64748b", fontWeight:"600" }}>{l}</span>
                </div>
              ))}
            </div>
            <BarChart labelWidth="180px"
              items={Object.entries(kpis.porJefe).sort((a,b)=>b[1].total-a[1].total).map(([label,v])=>({label,...v}))}
              activeConciliado={filtroConciliado}
              onClickItem={label => { setFiltroJefe(prev => prev.includes(label) ? prev.filter(x=>x!==label) : [...prev, label]); setPaginaActual(1); }}
              onClickSegment={(label, estado) => {
                setFiltroJefe(prev => prev.includes(label) ? prev.filter(x=>x!==label) : [...prev, label]);
                setFiltroConciliado(prev => prev === estado ? "" : estado);
                setPaginaActual(1);
              }}
            />
          </div>

        </div>
      )}

      {cargando ? (
        <div style={{ textAlign: "center", padding: "48px", color: "#64748b", fontSize: "14px" }}>Cargando conciliaciones...</div>
      ) : datosFiltrados.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px", background: "white", borderRadius: "12px",
                      border: "1px solid #e2e8f0", color: "#64748b", fontSize: "14px" }}>
          No hay registros de conciliación{hayFiltros ? " con los filtros aplicados" : " aún"}.
        </div>
      ) : (
        <>
          <div style={{ overflowX: "auto", borderRadius: "12px", border: "1px solid #e2e8f0", background: "white" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
              <thead>
                <tr>
                  <th style={thSt}>ID Nodo</th>
                  <th style={thSt}>Nodo</th>
                  <th style={thSt}>Ciudad</th>
                  <th style={thSt}>Categoría</th>
                  <th style={{ ...thSt, textAlign: "center" }}>% Dispo Real</th>
                  <th style={{ ...thSt, minWidth: "260px" }}>Causa Falla</th>
                  <th style={{ ...thSt, minWidth: "180px" }}>Observaciones</th>
                  <th style={thSt}>Prioridad</th>
                  <th style={thSt}>Mes</th>
                  <th style={thSt}>Agrupación</th>
                  <th style={thSt}>Aliado</th>
                  <th style={thSt}>Jefe Zona</th>
                  <th style={thSt}>Enviado por</th>
                  <th style={thSt}>Fecha Conciliación</th>
                  <th style={{ ...thSt, textAlign: "center", minWidth: "100px" }}>Conciliado</th>
                  <th style={{ ...thSt, minWidth: "180px" }}>Tipo Conciliación</th>
                  <th style={{ ...thSt, minWidth: "100px", textAlign: "center" }}>Cat. Conciliación</th>
                  <th style={{ ...thSt, minWidth: "200px" }}>Obs. Conciliación</th>
                  {puedeEditar && <th style={{ ...thSt, textAlign: "center" }}>Acción</th>}
                </tr>
              </thead>
              <tbody>
                {datosPagina.map((row, idx) => {
                  const valFalla      = getVal(row.id, "causa_falla")        ?? row.causa_falla        ?? "";
                  const valObs        = getVal(row.id, "observaciones")      ?? row.observaciones      ?? "";
                  const valConciliado = getVal(row.id, "conciliado")         ?? row.conciliado         ?? "";
                  const valTipoCon    = getVal(row.id, "tipo_conciliacion")  ?? row.tipo_conciliacion  ?? "";
                  const valCategCon   = getVal(row.id, "categoria_concilia") ?? row.categoria_concilia ?? "";
                  const valObsCon     = getVal(row.id, "obs_conciliacion")    ?? row.obs_conciliacion    ?? "";
                  const bgRow     = idx % 2 === 0 ? "#fff" : "#f8fafc";
                  return (
                    <tr key={row.id} style={{ background: bgRow, borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "7px 10px", fontWeight: "600" }}>{row.id_nodo}</td>
                      <td style={{ padding: "7px 10px" }}>{row.NOMBRE_NODO || "—"}</td>
                      <td style={{ padding: "7px 10px" }}>{row.ciudad || "—"}</td>
                      <td style={{ padding: "7px 10px", textAlign: "center" }}>
                        <span style={{ background: "#eff6ff", color: "#2563eb", borderRadius: "20px",
                                       padding: "2px 8px", fontSize: "11px", fontWeight: "700" }}>
                          {row.categoria || "—"}
                        </span>
                      </td>
                      <td style={{ padding: "7px 10px", textAlign: "center" }}>
                        {row.porc_dispo_real != null ? `${parseFloat(row.porc_dispo_real).toFixed(2)}%` : "—"}
                      </td>

                      {/* Causa Falla editable */}
                      <td style={{ padding: "6px 8px", minWidth: "260px" }}>
                        {puedeEditar ? (
                          <select value={valFalla}
                            onChange={e  => actualizarLocal(row.id, "causa_falla", e.target.value)}
                            onBlur={() => guardarCambio(row.id, "causa_falla")}
                            style={{ width: "100%", fontSize: "12px", padding: "4px" }}>
                            <option value="">-- Seleccionar --</option>
                            {causasFalla.map((c, i) => <option key={i} value={c}>{c}</option>)}
                          </select>
                        ) : <span>{row.causa_falla || "—"}</span>}
                      </td>

                      {/* Observaciones editable */}
                      <td style={{ padding: "6px 8px", minWidth: "180px" }}>
                        {puedeEditar ? (
                          <textarea value={valObs} rows={2}
                            onChange={e  => actualizarLocal(row.id, "observaciones", e.target.value)}
                            onBlur={() => guardarCambio(row.id, "observaciones")}
                            style={{ width: "100%", fontSize: "12px", resize: "vertical", borderRadius: "4px", border: "1px solid #cbd5e1", padding: "4px" }}
                            placeholder="Observación..." />
                        ) : <span style={{ whiteSpace: "pre-wrap" }}>{row.observaciones || "—"}</span>}
                      </td>

                      <td style={{ padding: "7px 10px", textAlign: "center" }}>
                        <span style={{ background: row.tipo_prioridad === "ALTO" ? "#fef2f2" : "#f0fdf4",
                                       color: row.tipo_prioridad === "ALTO" ? CLARO_RED : VERDE,
                                       borderRadius: "20px", padding: "2px 8px", fontSize: "11px", fontWeight: "700" }}>
                          {row.tipo_prioridad || "—"}
                        </span>
                      </td>
                      <td style={{ padding: "7px 10px" }}>{formatMes(row.mes_anio)}</td>
                      <td style={{ padding: "7px 10px" }}>{row.agrupacion || "—"}</td>
                      <td style={{ padding: "7px 10px" }}>{row.aliado || "—"}</td>
                      <td style={{ padding: "7px 10px" }}>{row.nombre_jefe || "—"}</td>
                      <td style={{ padding: "7px 10px", fontSize: "11px", color: "#64748b" }}>{row.nombre_usuario || "—"}</td>
                      <td style={{ padding: "7px 10px", fontSize: "11px", color: "#64748b" }}>
                        {formatFechaConciliacion(row.fecha_conciliacion)}
                      </td>
                      {/* Conciliado */}
                      <td style={{ padding: "6px 8px", textAlign: "center" }}>
                        {puedeEditar ? (
                          <select value={valConciliado}
                            onChange={e => guardarCambio(row.id, "conciliado", e.target.value)}
                            style={{ fontSize: "12px", padding: "4px 6px", borderRadius: "6px", width: "100%",
                                     border: valConciliado === "SI" ? "1.5px solid #16a34a" : "1.5px solid #e2e8f0",
                                     background: valConciliado === "SI" ? "#dcfce7" : valConciliado === "NO" ? "#fef2f2" : "white",
                                     color: valConciliado === "SI" ? "#16a34a" : valConciliado === "NO" ? CLARO_RED : "#475569",
                                     fontWeight: valConciliado ? "700" : "400" }}>
                            <option value="">-- Pendiente --</option>
                            <option value="SI">SI</option>
                            <option value="NO">NO</option>
                          </select>
                        ) : <span>{valConciliado || "-- Pendiente --"}</span>}
                      </td>

                      {/* Tipo Conciliación */}
                      <td style={{ padding: "6px 8px", minWidth: "180px" }}>
                        {puedeEditar ? (
                          <select value={valTipoCon}
                            onChange={e => guardarCambio(row.id, "tipo_conciliacion", e.target.value)}
                            style={{ fontSize: "12px", padding: "4px 6px", borderRadius: "6px",
                                     border: valTipoCon ? `1.5px solid #2563eb` : "1.5px solid #e2e8f0",
                                     background: valTipoCon ? "#eff6ff" : "white",
                                     color: valTipoCon ? "#2563eb" : "#475569", width: "100%" }}>
                            <option value="">-- Seleccionar --</option>
                            <option value="Cambio de Categoria">Cambio de Categoria</option>
                            <option value="Migracion y/o Desmonte Nodo">Migracion y/o Desmonte Nodo</option>
                            <option value="Otro">Otro</option>
                          </select>
                        ) : <span>{valTipoCon || "—"}</span>}
                      </td>

                      {/* Categoría Conciliación */}
                      <td style={{ padding: "6px 8px", textAlign: "center" }}>
                        {puedeEditar ? (
                          <select value={valCategCon}
                            onChange={e => guardarCambio(row.id, "categoria_concilia", e.target.value)}
                            style={{ fontSize: "12px", padding: "4px 6px", borderRadius: "6px", width: "100%",
                                     border: valCategCon ? "1.5px solid #7c3aed" : "1.5px solid #e2e8f0",
                                     background: valCategCon ? "#f5f3ff" : "white",
                                     color: valCategCon ? "#7c3aed" : "#475569" }}>
                            <option value="">--</option>
                            {["N0","N1","N2","N3","N4","N5","N6"].map(n => <option key={n} value={n}>{n}</option>)}
                          </select>
                        ) : <span>{valCategCon || "—"}</span>}
                      </td>

                      {/* Obs. Conciliación */}
                      <td style={{ padding: "6px 8px", minWidth: "200px" }}>
                        {puedeEditar ? (
                          <textarea value={valObsCon} rows={2}
                            onChange={e  => actualizarLocal(row.id, "obs_conciliacion", e.target.value)}
                            onBlur={() => guardarCambio(row.id, "obs_conciliacion")}
                            style={{ width: "100%", fontSize: "12px", resize: "vertical", borderRadius: "4px", border: "1px solid #cbd5e1", padding: "4px" }}
                            placeholder="Observación de conciliación..." />
                        ) : <span style={{ whiteSpace: "pre-wrap" }}>{valObsCon || "—"}</span>}
                      </td>

                      {puedeEditar && (
                        <td style={{ padding: "7px 10px", textAlign: "center" }}>
                          <button onClick={() => eliminar(row.id)}
                            style={{ background: "#fef2f2", color: CLARO_RED, border: `1px solid #fecaca`,
                                     borderRadius: "6px", padding: "4px 10px", fontSize: "11px",
                                     fontWeight: "600", cursor: "pointer" }}>
                            🗑 Quitar
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPags > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: "6px", marginTop: "16px", flexWrap: "wrap" }}>
              <button onClick={() => setPaginaActual(p => Math.max(1, p - 1))} disabled={paginaActual === 1}
                style={{ padding: "6px 14px", borderRadius: "7px", border: "1px solid #e2e8f0",
                         background: paginaActual === 1 ? "#f8fafc" : "white", cursor: paginaActual === 1 ? "default" : "pointer",
                         fontSize: "12px", fontWeight: "600", color: "#475569" }}>← Ant</button>
              {Array.from({ length: totalPags }, (_, i) => i + 1)
                .filter(p => Math.abs(p - paginaActual) <= 2)
                .map(p => (
                  <button key={p} onClick={() => setPaginaActual(p)}
                    style={{ padding: "6px 12px", borderRadius: "7px",
                             border: p === paginaActual ? `2px solid ${CLARO_RED}` : "1px solid #e2e8f0",
                             background: p === paginaActual ? "#fef2f2" : "white",
                             color: p === paginaActual ? CLARO_RED : "#475569",
                             fontWeight: p === paginaActual ? "700" : "500", cursor: "pointer", fontSize: "12px" }}>
                    {p}
                  </button>
                ))}
              <button onClick={() => setPaginaActual(p => Math.min(totalPags, p + 1))} disabled={paginaActual === totalPags}
                style={{ padding: "6px 14px", borderRadius: "7px", border: "1px solid #e2e8f0",
                         background: paginaActual === totalPags ? "#f8fafc" : "white",
                         cursor: paginaActual === totalPags ? "default" : "pointer",
                         fontSize: "12px", fontWeight: "600", color: "#475569" }}>Sig →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ConciliacionesOfensores;